import React, { useState, useEffect } from 'react';
import { notification } from 'antd';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../utils/apiInterceptor';
import config from '../config';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    secondary_email: '',
    phone: '',
    company_name: '',
    company_registration: '',
    apartment_number: '',
    property_address: '',
    country: 'GB', // Default to UK
    city: '',
    post_code: '',
    password: '',
    password_confirmation: '',
    service_type: 'LinenHire', // Default value
    number_of_properties: '1' // Default value
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([
    { id: 'LinenHire', name: 'Linen Hire' },
    { id: 'BabyEquipment', name: 'Baby Equipment' },
    { id: 'CleaningService', name: 'Cleaning Service' }
  ]);

  const [propertyRanges, setPropertyRanges] = useState([
    { id: '1', name: '1-5 Properties' },
    { id: '2', name: '6-10 Properties' },
    { id: '3', name: '11-25 Properties' },
    { id: '4', name: '26-50 Properties' },
    { id: '5', name: '51+ Properties' }
  ]);

  // Fetch countries on component mount
  useEffect(() => {
    // You would normally fetch this from your API
    // For now, using a sample list
    setCountries([
      { id: 'GB', name: 'United Kingdom' }
    ]);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {};

    // Required fields
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone', 
      'apartment_number', 'property_address', 'country', 
      'city', 'post_code', 'password', 'password_confirmation',
      'service_type', 'number_of_properties'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
        valid = false;
      }
    });

    // Validate name fields (only letters and spaces)
    const nameRegex = /^[a-zA-Z ]*$/;
    if (formData.first_name && !nameRegex.test(formData.first_name)) {
      newErrors.first_name = 'First name should only contain letters and spaces';
      valid = false;
    }
    if (formData.last_name && !nameRegex.test(formData.last_name)) {
      newErrors.last_name = 'Last name should only contain letters and spaces';
      valid = false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    // Validate phone (max 20 characters)
    if (formData.phone && formData.phone.length > 20) {
      newErrors.phone = 'Phone number should be maximum 20 characters';
      valid = false;
    }

    // Check passwords match
    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Passwords do not match';
      valid = false;
    }

    // Check password length
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password should be at least 6 characters';
      valid = false;
    }

    // Check if primary and secondary emails are the same
    if (formData.email && formData.secondary_email && formData.email === formData.secondary_email) {
      newErrors.secondary_email = 'Secondary email should be different from primary email';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      notification.error({
        message: 'Validation Error',
        description: 'Please correct the errors in the form.'
      });
      return;
    }

    setLoading(true);

    try {
      // Make API call to register endpoint
      const response = await apiService.post('/register', formData);
      
      if (response.status) {
        notification.success({
          message: 'Registration Successful',
          description: response.message || 'Your account is under review. Your dedicated account manager will be in touch with you shortly!'
        });
        
        // Reset form and navigate to login page
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          secondary_email: '',
          phone: '',
          company_name: '',
          company_registration: '',
          apartment_number: '',
          property_address: '',
          country: 'GB',
          city: '',
          post_code: '',
          password: '',
          password_confirmation: '',
          service_type: 'LinenHire',
          number_of_properties: '1'
        });
        
        // Redirect to login page after successful registration
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        notification.error({
          message: 'Registration Failed',
          description: response.error || 'An error occurred during registration.'
        });
        
        // If there are specific field errors returned from server
        if (response.errors) {
          setErrors(response.errors);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      notification.error({
        message: 'Registration Failed',
        description: error.message || 'An unexpected error occurred.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-golden-navy">
      <div className="w-full max-w-4xl p-8 space-y-6 bg-golden-navy text-white">
        <div className="text-center">
          <img src="/ico/gglogolarge.svg" alt="Golden Group Logo" className="mx-auto w-64" />
          <h2 className="mt-6 text-3xl font-bold">Create an Account</h2>
          <p className="mt-2">Please fill out the form below to register. Fields marked with * are required.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Section */}
          <div className="border border-golden-yellow rounded-md p-4">
            <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.first_name ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter your first name"
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-300">{errors.first_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.last_name ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter your last name"
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-300">{errors.last_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.email ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-300">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Secondary Email</label>
                <input
                  type="email"
                  name="secondary_email"
                  value={formData.secondary_email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.secondary_email ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter secondary email (optional)"
                />
                {errors.secondary_email && (
                  <p className="mt-1 text-sm text-red-300">{errors.secondary_email}</p>
                )}
                <p className="mt-1 text-xs text-golden-yellow">Optional but recommended for backup communication</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.phone ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-300">{errors.phone}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Company Information Section */}
          <div className="border border-golden-yellow rounded-md p-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Company Information (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-golden-yellow rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow"
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Company Registration Number</label>
                <input
                  type="text"
                  name="company_registration"
                  value={formData.company_registration}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-golden-yellow rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow"
                  placeholder="Enter registration number"
                />
              </div>
            </div>
          </div>
          
          {/* Address Information Section */}
          <div className="border border-golden-yellow rounded-md p-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Apartment/Building Number *</label>
                <input
                  type="text"
                  name="apartment_number"
                  value={formData.apartment_number}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.apartment_number ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter apartment/building number"
                />
                {errors.apartment_number && (
                  <p className="mt-1 text-sm text-red-300">{errors.apartment_number}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Address *</label>
                <input
                  type="text"
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.property_address ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter street address"
                />
                {errors.property_address && (
                  <p className="mt-1 text-sm text-red-300">{errors.property_address}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.city ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter city"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-300">{errors.city}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Post Code *</label>
                <input
                  type="text"
                  name="post_code"
                  value={formData.post_code}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.post_code ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter postal code"
                />
                {errors.post_code && (
                  <p className="mt-1 text-sm text-red-300">{errors.post_code}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Country *</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.country ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                >
                  <option value="">Select Country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="mt-1 text-sm text-red-300">{errors.country}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Service Information Section */}
          <div className="border border-golden-yellow rounded-md p-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Service Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Service Type *</label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.service_type ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                >
                  <option value="">Select Service Type</option>
                  {serviceTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.service_type && (
                  <p className="mt-1 text-sm text-red-300">{errors.service_type}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Number of Properties *</label>
                <select
                  name="number_of_properties"
                  value={formData.number_of_properties}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.number_of_properties ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                >
                  <option value="">Select Property Range</option>
                  {propertyRanges.map(range => (
                    <option key={range.id} value={range.id}>
                      {range.name}
                    </option>
                  ))}
                </select>
                {errors.number_of_properties && (
                  <p className="mt-1 text-sm text-red-300">{errors.number_of_properties}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Password Section */}
          <div className="border border-golden-yellow rounded-md p-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Security Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.password ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Enter password"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-300">{errors.password}</p>
                )}
                <p className="mt-1 text-xs text-golden-yellow">Password must be at least 6 characters long</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Confirm Password *</label>
                <input
                  type="password"
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.password_confirmation ? 'border-red-500' : 'border-golden-yellow'
                  } rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow`}
                  placeholder="Confirm your password"
                />
                {errors.password_confirmation && (
                  <p className="mt-1 text-sm text-red-300">{errors.password_confirmation}</p>
                )}
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="flex items-start mt-4">
            <div className="flex items-center h-5">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                className="focus:ring-golden-yellow h-4 w-4 text-golden-yellow border-golden-yellow bg-golden-navy rounded"
                required
              />
            </div>
             
          </div>

          {/* Submit Buttons */}
          <div className="flex space-x-4 mt-6">
            <button
              type="button" 
              onClick={() => navigate('/login')}
              className="flex-1 py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white hover:bg-golden-yellow hover:text-golden-navy transition-colors"
            >
              Back to Login
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 bg-white text-golden-navy border border-transparent rounded-md shadow-sm text-sm font-medium hover:bg-golden-yellow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;